import React, { useState, useMemo } from 'react';
import { Formik, Field, Form } from 'formik';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    IconButton,
    CssBaseline,
    alpha,
    Paper,
    Snackbar,
    Alert,
    Container,
    ListItemIcon,
    List,
    ListItem,
    ListItemText,
    Slide,
    InputAdornment,
} from "@mui/material";
import { object, string } from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import ArrowRightIcon from '@mui/icons-material/ArrowRight'; 
import BusinessIcon from '@mui/icons-material/Business'; 
import DescriptionIcon from '@mui/icons-material/Description'; 

function convertToUrlFriendlyString(input) {
    return input
        .toLowerCase()
        .replace(/[^\w\s-]|_/g, "")
        .replace(/\s+/g, "-")
        .replace(/^[^a-z0-9]+|[^\da-z0-9]+$/g, "");
}

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const CreateOrganizationDialog = ({ onCreate, open }) => { 
    const [dialogOpen, setDialogOpen] = useState(false); 
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const handleOpenDialog = () => setDialogOpen(true);
    const handleCloseDialog = () => setDialogOpen(false);

    const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
        if (onCreate) {
            onCreate({
                name: convertToUrlFriendlyString(values.descriptiveName),
                descriptiveName: values.descriptiveName,
                description: values.description
            });
        } else {
            console.warn("CreateOrganizationDialog: onCreate prop is not defined.");
        }
        setSubmitting(false);
        
        setSnackbarMessage(`Organization "${values.descriptiveName}" submission processed.`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        handleCloseDialog();
        resetForm();
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const illustrationPath = ``;
    const showIllustration = true;

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={open} 
                onClose={handleCloseDialog} 
                TransitionComponent={Transition}
                PaperProps={{sx: {borderRadius: '24px',boxShadow: 10}}}
            >
                <DialogTitle sx={{ pt: 3, pb: 2, px: 3.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/ct_logo_black.png" 
                                alt="Candidate Tools Logo"
                                style={{
                                    height: '28px',
                                    marginRight: '12px',
                                    verticalAlign: 'middle',
                                }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                                Create New Organization
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            sx={{ color: "text.secondary" }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                
                <Formik
                initialValues={{ descriptiveName: "", description: "" }}
                validationSchema={object({
                    descriptiveName: string()
                        .trim('Name cannot be only spaces')
                        .min(5, 'Name should be at least 5 characters')
                        .required('Name is required'),
                    description: string()
                        .trim('Description cannot be only spaces')
                        .min(5, 'Description should be at least 5 characters')
                        .required('Description is required'),
                })}
                onSubmit={(values, { setSubmitting }) => {
                    onCreate({
                        name: convertToUrlFriendlyString(values.descriptiveName),
                        descriptiveName: values.descriptiveName,
                        description: values.description
                    }); // pass form data to onCreate
                    setSubmitting(false);
                }}
            >
                    {({ errors, touched, isSubmitting }) => (
                        <Form>
                            <DialogContent sx={{ px: 3.5, py: 3.5 }}>
                                {showIllustration && illustrationPath && ( // Conditionally render based on path too
                                    <Box sx={{ textAlign: 'center', mb: 3.5, mt: 1 }}>
                                        <img
                                            src={illustrationPath}
                                            alt="Create Organization Illustration"
                                            style={{ width: '110px', height: 'auto', opacity: 0.95 }}
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2.5 }}>
                                            Define the core details of your new workspace.
                                        </Typography>
                                    </Box>
                                )}
                                <Box mb={3.5}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        required
                                        id="descriptiveName"
                                        name="descriptiveName"
                                        label="Organization Name"
                                        variant="filled"
                                        helperText={touched.descriptiveName ? errors.descriptiveName : "e.g., QuantumLeap Inc., Project Nova"}
                                        error={touched.descriptiveName && Boolean(errors.descriptiveName)}
                                        InputLabelProps={{ sx: { fontSize: '1rem' } }}
                                        InputProps={{
                                            startAdornment: ( <InputAdornment position="start"> <BusinessIcon sx={{ color: "text.secondary", fontSize: '1.25rem' }} /> </InputAdornment> ),
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        required
                                        multiline
                                        rows={4}
                                        id="description"
                                        name="description"
                                        label="Organization Description"
                                        variant="filled"
                                        helperText={touched.description ? errors.description : "Briefly outline its purpose, goals, or main activities."}
                                        error={touched.description && Boolean(errors.description)}
                                        InputLabelProps={{ sx: { fontSize: '1rem' } }}
                                        InputProps={{
                                            startAdornment: ( <InputAdornment position="start" sx={{mt: -7.5, alignItems: 'flex-start'}}> <DescriptionIcon sx={{ color: "text.secondary", fontSize: '1.25rem' }} /> </InputAdornment> ),
                                        }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                                {/* Cancel button now uses the updated handleCloseDialog */}
                                <Button onClick={handleCloseDialog} sx={{ color: "text.primary", textTransform: 'none', fontWeight: 500, padding: '8px 16px', '&:hover': { backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.12) } }} > Cancel </Button>
                                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting} sx={{ textTransform: 'none', fontWeight: 600, padding: '10px 28px', borderRadius: '12px', '&:hover': { backgroundColor: "primary.dark", transform: 'scale(1.02)', }, transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out', }} > {isSubmitting ? "Creating..." : "Create Organization"} </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant={"standard"} sx={{ width: '100%', boxShadow: 6 }} > {snackbarMessage} </Alert>
            </Snackbar>
        </>
    );
};

export default CreateOrganizationDialog;